import { Box, Grid, Stack, Typography } from '@mui/material';
import { skipToken } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { url, useConfiguredUrl } from '../../appRouter/url';
import { ContentLoading, EasyBreadcrumbs, NoContentInfo, ThemaSummary } from '../../components/general';
import { AusschreibungRueckmeldungenCard } from '../../components/general/Ausschreibung/AusschreibungRueckmeldungenCard.tsx';
import { AusschreibungsprozessCard } from '../../components/general/Ausschreibung/AusschreibungsprozessCard.tsx';
import { shouldHideAusschreibungCards } from '../../components/general/Ausschreibung/shouldHideAusschreibungCards.ts';
import { isTerminvorschlaegePruefenEnabled } from '../../components/general/Ausschreibung/terminvorschlaegePruefenFeature.ts';
import { FailedQueryAlert } from '../../components/general/ErrorAlert';
import { type Leistung, type Veranstaltung } from '../../dtos';
import { useNormalizedParams } from '../../hooks/useNormalizedParams';
import { usePageTitle } from '../../hooks/usePageTitle';
import { trpc } from '../../trpc';
import { formatDate } from '../../utils';
import { getAllLeistungen } from '../../utils/teilleistung';
import { formatBuchungsnummern } from '../../utils/themaProdukte';
import { TerminAblaufCard } from './TerminAblaufCard';
import { TerminExperteCard } from './TerminExperteCard';
import { TerminLocationCard } from './TerminLocationCard';
import { TerminWarningsCard } from './TerminWarningsCard';
import { VeranstaltungDetailsHeader } from './VeranstaltungDetailsHeader.tsx';
import { VorschlaegePruefen } from './VorschlaegePruefen.tsx';

export const VeranstaltungDetailsPage = () => {
  const { configuredUrl } = useConfiguredUrl();
  const [searchParams] = useSearchParams();
  const { akaVeranstaltungId } = useNormalizedParams();
  const navigate = useNavigate();
  const [pruefung, setPruefung] = useState<{
    leistungId: Leistung['leistungId'];
    teilleistungId: string;
  } | null>(null);

  if (akaVeranstaltungId === undefined) {
    throw new TypeError('Die Veranstaltungs-Id fehlt in der URL.');
  }

  const isAkaVeranstaltungId = akaVeranstaltungId.startsWith('V') || akaVeranstaltungId.startsWith('E');

  const veranstaltungQuery = trpc.veranstaltung.getVeranstaltungByAkaVeranstaltungId.useQuery(
    { akaVeranstaltungId },
    { enabled: isAkaVeranstaltungId },
  );

  usePageTitle(getTitle(veranstaltungQuery.data));

  const themaQuery = trpc.thema.getThemaByAkaThemaId.useQuery(veranstaltungQuery.data?.thema.akaThemaId ?? skipToken);

  if (veranstaltungQuery.error && veranstaltungQuery.error.data?.code === 'UNPROCESSABLE_CONTENT') {
    return <NoContentInfo content="Invalide Aka Veranstaltung Id" />;
  } else if (veranstaltungQuery.error && veranstaltungQuery.error.data?.code !== 'NOT_FOUND') {
    return <FailedQueryAlert error={veranstaltungQuery.error} query="Veranstaltung" />;
  } else if (themaQuery.error && themaQuery.error.data?.code !== 'NOT_FOUND') {
    return <FailedQueryAlert error={themaQuery.error} query="Thema" />;
  } else if (themaQuery.isLoading || veranstaltungQuery.isLoading) {
    return <ContentLoading />;
  }

  const veranstaltung = veranstaltungQuery.data;
  if (!veranstaltung) {
    return (
      <NoContentInfo
        content={
          <Typography>
            Keine Veranstaltung gefunden. Sie wurde entweder gelöscht oder ist nicht in Viva-Planung enthalten. Bitte
            suche die Veranstaltung in SAP oder probiere es im&nbsp;
            <Link
              target="_blank"
              rel="noopener"
              to={
                searchParams.has('sapId')
                  ? configuredUrl.sapAdapter.veranstaltung(Number(searchParams.get('sapId')))
                  : configuredUrl.sapAdapter.root()
              }
            >
              Viva SAP-Adapter
            </Link>
          </Typography>
        }
      />
    );
  }

  const thema = themaQuery.data?.thema;
  if (!thema) {
    return <NoContentInfo content="Kein Thema gefunden." />;
  }

  if (veranstaltung.akaDurchgangId) {
    void navigate(url.durchgang.detailsForModultermin(veranstaltung.akaDurchgangId, akaVeranstaltungId));
  }

  const leistungen = shouldHideAusschreibungCards(veranstaltung.vivaStatus)
    ? []
    : getAllLeistungen(veranstaltung.teilleistungen);

  return (
    <Stack spacing={2} sx={{ paddingTop: 2 }}>
      <EasyBreadcrumbs
        items={[
          { label: 'Dashboard', href: url.root() },
          { label: 'Thema', href: url.thema.details(thema.akaThemaId) },
          { label: 'Veranstaltung', href: '' },
        ]}
      />
      <Box display="grid" gridTemplateColumns="1fr 320px" gap={3}>
        <VeranstaltungDetailsHeader veranstaltung={veranstaltung} />
        <ThemaSummary thema={thema} showThemaLink />
      </Box>
      <TerminWarningsCard akaVeranstaltungId={veranstaltung.akaVeranstaltungId} />
      <Grid container spacing={2}>
        {leistungen.map((leistung) => (
          <Box
            key={leistung.leistungId}
            display="grid"
            gridTemplateColumns={{ xs: '1fr', lg: '1fr 2fr' }}
            gap={2}
            width="100%"
          >
            <AusschreibungsprozessCard
              leistung={leistung}
              showManuellerAusschreibungsprozessInfo={veranstaltung.manuellerAusschreibungsprozess}
            />
            <AusschreibungRueckmeldungenCard
              leistung={leistung}
              teilleistungId={
                veranstaltung.teilleistungen.find(
                  (teilleistung) => teilleistung.leistung.leistungId === leistung.leistungId,
                )?.teilleistungId
              }
              onVorschlaegePruefen={
                isTerminvorschlaegePruefenEnabled()
                  ? (teilleistungId) => {
                      setPruefung({ leistungId: leistung.leistungId, teilleistungId });
                    }
                  : undefined
              }
            />
          </Box>
        ))}
        <Grid size={{ xs: 12, xl: 6, md: 6 }}>
          <TerminExperteCard key="experte" veranstaltung={veranstaltung} thema={thema} />
        </Grid>
        <Grid size={{ xs: 12, xl: 6, md: 6 }}>
          <Stack spacing={2}>
            <TerminLocationCard key="location" thema={thema} veranstaltung={veranstaltung} />
            <TerminAblaufCard
              key="ablauf"
              veranstaltung={veranstaltung}
              isTerminreihe={thema.planungsinformation?.istTerminreihe ?? false}
            />
          </Stack>
        </Grid>
      </Grid>
      {pruefung && (
        <VorschlaegePruefen
          thema={thema}
          veranstaltung={veranstaltung}
          leistungId={pruefung.leistungId}
          activeTeilleistungId={pruefung.teilleistungId}
          onClose={() => {
            setPruefung(null);
          }}
        />
      )}
    </Stack>
  );
};

function getTitle(veranstaltung: Veranstaltung | undefined): string {
  const buchungsnummern = veranstaltung && formatBuchungsnummern(veranstaltung.thema);
  const startDate = veranstaltung?.ablauf[0]?.start;

  if (!buchungsnummern || !startDate) {
    return 'Veranstaltung';
  }

  return `${buchungsnummern} | ${formatDate(startDate)} | Veranstaltung`;
}
